import { FormattedMessage, useIntl } from "react-intl";
import { Tooltip } from "@/components/common/Tooltip";

/**
 * Marks a match result whose AI judgement saw organisational-record entries (GENESIS-0060).
 *
 * genesis answers such a result only to principals holding `read:organizational-record`, as a whole
 * row. Whoever sees this badge therefore holds the capability; the badge tells them that colleagues
 * without it do not see the result at all, so they can explain the shorter list instead of
 * suspecting a failed run. Normal case on purpose: all-caps is reserved for navigation, the AI
 * prompt eyebrow and the mandatory badge (SPARK-0026); a passive origin marker is none of those.
 *
 * Standing alone, the explanation is a `<Tooltip>`, so it reaches keyboard and touch as well
 * (UI_CONVENTIONS, Tooltip & InfoTooltip). Inside a button (`insideInteractive`) the tooltip's
 * focusable trigger would nest one interactive element in another, so the badge falls back to the
 * native `title` there; the opened result repeats the badge with the full tooltip.
 */
export function OrganizationalRecordBadge({
  id,
  insideInteractive = false,
}: {
  id?: string;
  insideInteractive?: boolean;
}) {
  const intl = useIntl();
  const explanation = intl.formatMessage({
    id: "match.result.organizationalRecordBadgeTitle",
    defaultMessage: "Visible to record readers only",
  });
  // shrink-0 + whitespace-nowrap: next to a truncating name only the name gives way, the badge never wraps.
  const pill = (
    <span
      id={id}
      className={`inline-flex shrink-0 items-center whitespace-nowrap rounded-full bg-ink/[0.06] px-2 py-0.5 text-smycles-micro font-semibold leading-none text-ink-muted ring-1 ring-ink/10 ${insideInteractive ? "" : "cursor-help"}`}
      title={insideInteractive ? explanation : undefined}
    >
      <FormattedMessage id="match.result.organizationalRecordBadge" defaultMessage="With organisational record" />
    </span>
  );
  if (insideInteractive) {
    return pill;
  }
  return (
    <Tooltip content={explanation} className="shrink-0">
      {pill}
    </Tooltip>
  );
}
